import { useEffect } from "react";
import { useCreate, useDelete, useOne, useUpdate } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { Button, Form, Input, Popconfirm, Space, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import {
  AppImageUploader,
  AppRichText,
  AppTitle,
  EFileTypes,
} from "components";
import { MedicineConferencePageHeader } from "./types";

export const MedicineConferencePageForm = () => {
  const [form] = Form.useForm<MedicineConferencePageHeader>();
  const { data, isLoading, refetch } = useOne<MedicineConferencePageHeader>({
    resource: "medicine-conference",
    dataProviderName: "medicineContestsDataProvider",
    id: "conference",
  });
  const { mutate: createHeader, isLoading: creatingHeader } = useCreate();
  const { mutate: updateHeader, isLoading: updatingHeader } = useUpdate();
  const { mutate: deleteHeader, isLoading: deletingHeader } = useDelete();

  const header = data?.data;
  const headerId = header?.id || form.getFieldValue("id");
  const saving = creatingHeader || updatingHeader;

  useEffect(() => {
    form.setFieldsValue({
      id: header?.id,
      bannerImageUrl: header?.bannerImageUrl || "",
      htmlText: header?.htmlText || "",
    });
  }, [form, header]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      id: values.id || null,
    };
    const callbacks = {
      onSuccess: async (response: any) => {
        const savedHeader = response?.data as MedicineConferencePageHeader;
        message.success("Страница конференции сохранена");

        if (savedHeader?.id) {
          form.setFieldValue("id", savedHeader.id);
        }

        await refetch();
      },
      onError: (error: any) => {
        message.error(
          error?.message || "Не удалось сохранить страницу конференции"
        );
      },
    };

    if (payload.id) {
      updateHeader(
        {
          dataProviderName: "medicineContestsDataProvider",
          resource: "medicine-conference",
          id: payload.id,
          values: payload,
        },
        callbacks
      );
      return;
    }

    createHeader(
      {
        dataProviderName: "medicineContestsDataProvider",
        resource: "medicine-conference",
        values: payload,
      },
      callbacks
    );
  };

  const handleDelete = () => {
    deleteHeader(
      {
        dataProviderName: "medicineContestsDataProvider",
        resource: "medicine-conference",
        id: headerId || "conference",
      },
      {
        onSuccess: async () => {
          message.success("Страница конференции удалена");
          form.resetFields();
          await refetch();
        },
        onError: (error: any) => {
          message.error(
            error?.message || "Не удалось удалить страницу конференции"
          );
        },
      }
    );
  };

  return (
    <Show
      isLoading={isLoading}
      title={<AppTitle canGoBack>Страница конференции</AppTitle>}
      breadcrumb={false}
      headerButtons={<></>}
      footerButtons={null}
      goBack={false}
    >
      <Form<MedicineConferencePageHeader> form={form} layout="vertical">
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>

        <Form.Item name="bannerImageUrl" label="Баннер">
          <AppImageUploader
            type="MEDICINE"
            entityId="conference/banner"
            serviceType={EFileTypes.MEDICINE}
            cropperProps={{
              modalTitle: "Редактировать баннер",
            }}
            uploadProps={{
              multiple: false,
              children: "Баннер",
              listType: "picture-card",
            }}
          />
        </Form.Item>

        <Form.Item name="htmlText" label="HTML-текст">
          <AppRichText />
        </Form.Item>

        <Space>
          <Button type="primary" loading={saving} onClick={handleSubmit}>
            Сохранить
          </Button>

          <Popconfirm
            title="Удалить страницу конференции?"
            okText="Удалить"
            cancelText="Отмена"
            onConfirm={handleDelete}
            disabled={!headerId}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deletingHeader}
              disabled={!headerId}
            >
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      </Form>
    </Show>
  );
};
